/**
 * Compact action items panel for Word add-in (320px task pane).
 *
 * Collapsible sub-panel shown in the "show more" accordion.
 * Supports: list, filter, inline create, complete/reopen, delete.
 */

import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ListChecks,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  AlertTriangle,
  Calendar,
  Pencil,
  Check,
} from 'lucide-react'
import {
  useActionItems,
  useCreateActionItem,
  useUpdateActionItem,
  useCompleteActionItem,
  useReopenActionItem,
  useDeleteActionItem,
} from '../../hooks/useNegotiations'
import type { ActionItem, NegotiationParty } from '../../services/negotiationsApi'

type Filter = 'all' | 'mine' | 'blockers' | 'overdue'

const PRIORITY_STYLES: Record<string, string> = {
  LOW: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
  NORMAL: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  HIGH: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  URGENT: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

interface ActionItemsPanelProps {
  negotiationId: number
  items?: ActionItem[] | undefined
  isLoading?: boolean
  parties: NegotiationParty[]
  actorId?: string
  alwaysExpanded?: boolean
}

export function ActionItemsPanel({
  negotiationId,
  items: itemsProp,
  isLoading: isLoadingProp,
  parties,
  actorId,
  alwaysExpanded,
}: ActionItemsPanelProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded ?? false)

  // Self-fetch when used standalone in tab mode (no items prop passed)
  const selfFetch = useActionItems(itemsProp === undefined ? negotiationId : undefined)
  const items = itemsProp ?? selfFetch.data
  const isLoading = isLoadingProp ?? selfFetch.isLoading
  const [filter, setFilter] = useState<Filter>('all')
  const [showCreate, setShowCreate] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newPriority, setNewPriority] = useState<'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'>('NORMAL')
  const [newDueDate, setNewDueDate] = useState('')
  const [newAssignee, setNewAssignee] = useState('')

  const createMut = useCreateActionItem()
  const completeMut = useCompleteActionItem()
  const reopenMut = useReopenActionItem()
  const deleteMut = useDeleteActionItem()

  const myParty = useMemo(
    () => parties.find((p) => p.actor?.koblink_id === actorId && p.is_active),
    [parties, actorId],
  )

  const activeItems = useMemo(() => items ?? [], [items])

  const doneCount = useMemo(
    () => activeItems.filter((i) => i.status === 'DONE').length,
    [activeItems],
  )

  const blockerCount = useMemo(
    () => activeItems.filter((i) => i.is_blocker && i.status !== 'DONE').length,
    [activeItems],
  )

  const filtered = useMemo(() => {
    switch (filter) {
      case 'mine':
        return activeItems.filter((i) => myParty && i.assigned_party?.koblink_id === myParty.actor?.koblink_id)
      case 'blockers':
        return activeItems.filter((i) => i.is_blocker && i.status !== 'DONE')
      case 'overdue':
        return activeItems.filter((i) => i.due_date && new Date(i.due_date) < new Date() && i.status !== 'DONE')
      default:
        return activeItems
    }
  }, [activeItems, filter, myParty])

  const handleCreate = async () => {
    const title = newTitle.trim()
    if (!title) return
    await createMut.mutateAsync({
      negotiationId,
      title,
      priority: newPriority,
      due_date: newDueDate || undefined,
      assigned_party_id: newAssignee ? Number(newAssignee) : undefined,
    })
    setNewTitle('')
    setNewPriority('NORMAL')
    setNewDueDate('')
    setNewAssignee('')
  }

  const handleToggle = (item: ActionItem) => {
    if (item.status === 'DONE') {
      reopenMut.mutate({ negotiationId, itemId: item.id })
    } else {
      completeMut.mutate({ negotiationId, itemId: item.id })
    }
  }

  const handleDelete = (itemId: number) => {
    deleteMut.mutate({ negotiationId, itemId })
  }

  if (isLoading || !items) return null

  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: t('negotiate.actionItems.filter.all') },
    { key: 'mine', label: t('negotiate.actionItems.filter.mine') },
    { key: 'blockers', label: t('negotiate.actionItems.filter.blockers') },
    { key: 'overdue', label: t('negotiate.actionItems.filter.overdue') },
  ]

  const expanded = alwaysExpanded || isExpanded

  return (
    <div className={alwaysExpanded ? '' : 'border-b border-neutral-100 dark:border-neutral-800'}>
      {/* Header toggle — hidden in tab mode */}
      {!alwaysExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
        >
          <ListChecks size={10} />
          {t('negotiate.actionItems.title')}
          {activeItems.length > 0 && (
            <span className="text-neutral-400 dark:text-neutral-500 normal-case tracking-normal">
              {doneCount}/{activeItems.length}
            </span>
          )}
          {blockerCount > 0 && (
            <span className="px-1 py-0.5 text-[9px] font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 normal-case tracking-normal">
              {blockerCount} {t('negotiate.actionItems.blockers')}
            </span>
          )}
          <span className="flex-1" />
          {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        </button>
      )}

      {expanded && (
        <div className="animate-fade-in">
          {/* Filter pills */}
          <div className="px-3 pb-1.5 flex items-center gap-1">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full transition-colors ${
                  filter === f.key
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Items list */}
          <div className="px-3 pb-2">
            {filtered.length === 0 ? (
              <p className="py-3 text-center text-[10px] text-neutral-400 dark:text-neutral-500">
                {activeItems.length === 0
                  ? t('negotiate.actionItems.empty')
                  : t('negotiate.actionItems.emptyFilter')}
              </p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((item) => (
                  <ActionItemRow
                    key={item.id}
                    item={item}
                    negotiationId={negotiationId}
                    parties={parties}
                    onToggle={handleToggle}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Inline create */}
          {showCreate ? (
            <div className="px-3 pb-2 space-y-1.5">
              {/* Row 1: Title */}
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate()
                    if (e.key === 'Escape') { setShowCreate(false); setNewTitle('') }
                  }}
                  placeholder={t('negotiate.actionItems.titlePlaceholder')}
                  className="flex-1 px-2 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  autoFocus
                />
                <button
                  onClick={() => { setShowCreate(false); setNewTitle(''); setNewPriority('NORMAL'); setNewDueDate(''); setNewAssignee('') }}
                  className="p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                >
                  <X size={10} />
                </button>
              </div>

              {/* Row 2: Priority + Due date */}
              <div className="flex items-center gap-1.5">
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as typeof newPriority)}
                  className="flex-1 px-1.5 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-primary-400"
                >
                  <option value="LOW">{t('common.priority.low', 'Low')}</option>
                  <option value="NORMAL">{t('common.priority.normal', 'Normal')}</option>
                  <option value="HIGH">{t('common.priority.high', 'High')}</option>
                  <option value="URGENT">{t('common.priority.urgent', 'Urgent')}</option>
                </select>
                <input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  className="flex-1 px-1.5 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-primary-400"
                />
              </div>

              {/* Row 3: Assignee */}
              <select
                value={newAssignee}
                onChange={(e) => setNewAssignee(e.target.value)}
                className="w-full px-1.5 py-1 text-[10px] rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-1 focus:ring-primary-400"
              >
                <option value="">{t('negotiate.actionItems.unassigned', 'Unassigned')}</option>
                {parties.filter((p) => p.is_active).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.party_label || p.tag_name}
                  </option>
                ))}
              </select>

              {/* Row 4: Add button */}
              <button
                onClick={handleCreate}
                disabled={!newTitle.trim() || createMut.isPending}
                className="w-full px-2 py-1 text-[10px] font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded transition-colors"
              >
                {t('negotiate.actionItems.add')}
              </button>
            </div>
          ) : (
            <div className="px-3 pb-2">
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1 text-[10px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                <Plus size={10} />
                {t('negotiate.actionItems.new')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Action Item Row ──────────────────────────────────────

function ActionItemRow({
  item,
  negotiationId,
  parties,
  onToggle,
  onDelete,
}: {
  item: ActionItem
  negotiationId: number
  parties: NegotiationParty[]
  onToggle: (item: ActionItem) => void
  onDelete: (id: number) => void
}) {
  const { t } = useTranslation()
  const isDone = item.status === 'DONE'
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(item.title)
  const [editPriority, setEditPriority] = useState(item.priority)
  const [editDueDate, setEditDueDate] = useState(item.due_date?.split('T')[0] ?? '')
  const [editAssignee, setEditAssignee] = useState<number | ''>(
    // Find party ID from assigned_party actor
    parties.find(p => p.actor?.koblink_id === item.assigned_party?.koblink_id)?.id ?? '',
  )
  const updateMut = useUpdateActionItem()

  const dueDateStr = item.due_date
    ? new Date(item.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null

  const handleSaveEdit = useCallback(async () => {
    if (!editTitle.trim()) return
    await updateMut.mutateAsync({
      negotiationId,
      itemId: item.id,
      title: editTitle.trim(),
      priority: editPriority,
      due_date: editDueDate || null,
      assigned_party_id: editAssignee || null,
    })
    setIsEditing(false)
  }, [negotiationId, item.id, editTitle, editPriority, editDueDate, editAssignee, updateMut])

  const handleStartEdit = useCallback(() => {
    setEditTitle(item.title)
    setEditPriority(item.priority)
    setEditDueDate(item.due_date?.split('T')[0] ?? '')
    setEditAssignee(
      parties.find(p => p.actor?.koblink_id === item.assigned_party?.koblink_id)?.id ?? '',
    )
    setIsEditing(true)
  }, [item, parties])

  if (isEditing) {
    return (
      <div className="py-1.5 px-1 rounded bg-neutral-50 dark:bg-neutral-800/50 space-y-1">
        <input
          type="text"
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          className="w-full text-[10px] px-1.5 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setIsEditing(false) }}
        />
        <div className="flex items-center gap-1">
          <select
            value={editPriority}
            onChange={e => setEditPriority(e.target.value as ActionItem['priority'])}
            className="text-[9px] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          >
            <option value="LOW">{t('negotiate.priority.low', 'Low')}</option>
            <option value="NORMAL">{t('negotiate.priority.normal', 'Normal')}</option>
            <option value="HIGH">{t('negotiate.priority.high', 'High')}</option>
            <option value="URGENT">{t('negotiate.priority.urgent', 'Urgent')}</option>
          </select>
          <input
            type="date"
            value={editDueDate}
            onChange={e => setEditDueDate(e.target.value)}
            className="text-[9px] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          />
        </div>
        <div className="flex items-center gap-1">
          <select
            value={editAssignee}
            onChange={e => setEditAssignee(e.target.value ? Number(e.target.value) : '')}
            className="flex-1 text-[9px] px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
          >
            <option value="">{t('negotiate.unassigned', 'Unassigned')}</option>
            {parties.filter(p => p.is_active).map(p => (
              <option key={p.id} value={p.id}>{p.actor?.display_name || p.tag_name}</option>
            ))}
          </select>
          <button
            onClick={handleSaveEdit}
            disabled={updateMut.isPending || !editTitle.trim()}
            className="shrink-0 p-0.5 text-success-600 hover:text-success-700 disabled:opacity-50"
          >
            <Check size={12} />
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="shrink-0 p-0.5 text-neutral-400 hover:text-neutral-600"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="group flex items-start gap-1.5 py-1 rounded hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {/* Checkbox */}
      <button
        onClick={() => onToggle(item)}
        className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded border transition-colors ${
          isDone
            ? 'bg-success-500 border-success-500 text-white'
            : 'border-neutral-300 dark:border-neutral-600 hover:border-primary-400'
        }`}
      >
        {isDone && (
          <svg viewBox="0 0 14 14" className="w-full h-full">
            <path d="M3.5 7l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Title + badges */}
        <div className="flex items-center gap-1">
          <span
            className={`text-[10px] leading-tight truncate ${
              isDone
                ? 'line-through text-neutral-400 dark:text-neutral-500'
                : 'text-neutral-800 dark:text-neutral-200'
            }`}
          >
            {item.title}
          </span>
          {item.is_blocker && !isDone && (
            <span className="shrink-0 px-1 py-0.5 text-[8px] font-bold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 uppercase">
              <AlertTriangle size={8} className="inline" />
            </span>
          )}
          {item.priority !== 'NORMAL' && !isDone && (
            <span className={`shrink-0 px-1 py-0.5 text-[8px] font-medium rounded ${PRIORITY_STYLES[item.priority]}`}>
              {item.priority}
            </span>
          )}
        </div>

        {/* Detail line: assignee + due date */}
        {(item.assigned_party || dueDateStr) && !isDone && (
          <div className="flex items-center gap-1.5 mt-0.5 text-[9px] text-neutral-400 dark:text-neutral-500">
            {item.assigned_party && (
              <span className="flex items-center gap-0.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-400" />
                {item.assigned_party.display_name}
              </span>
            )}
            {dueDateStr && (
              <span className={`flex items-center gap-0.5 ${item.due_date && new Date(item.due_date) < new Date() && item.status !== 'DONE' ? 'text-red-500 font-medium' : ''}`}>
                <Calendar size={8} />
                {dueDateStr}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Edit — visible on hover */}
      {!isDone && (
        <button
          onClick={handleStartEdit}
          className="shrink-0 mt-0.5 p-0.5 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-primary-500 transition-all"
          title={t('negotiate.editTask', 'Edit')}
        >
          <Pencil size={10} />
        </button>
      )}

      {/* Delete — visible on hover */}
      <button
        onClick={() => onDelete(item.id)}
        className="shrink-0 mt-0.5 p-0.5 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-500 transition-all"
      >
        <X size={10} />
      </button>
    </div>
  )
}
